import React, { Fragment, ReactNode, useState, type JSX } from 'react';
import { Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues } from 'formik';
import { Progess } from '../components/Checkout/Progress';
import { CartListTraining, CheckoutOrderType, FormValues } from '../components/Checkout/Checkout.interface';
import { FormErrors } from './FormErrors';
import { OverviewItem } from '../components/Checkout/OverviewItem';
import { getExamentrainingDatesLabel } from '../models/Examentraining';
import { cart } from '../components/Cart/Cart';
import { CartTotal } from '../components/Checkout/CartList/CartTotal';
import { CalculationResponse, CouponForm } from '../components/Checkout/Form/CouponForm';
import { Article } from '../models/Article';
import { cartItemIsArticle } from '../components/Checkout/CartList/CartList';

type Total = CalculationResponse['total'];

export interface WizardProps {
  children: any;
  initialValues: FormikValues;
  error: string|string[];
  onSubmit?: (formValues: FormValues, ) => void;
  isPreRegisterOnly: boolean;
  notice: string;
  cartList: any[];
  total: any;
  orderType: CheckoutOrderType;
  setTotal: (total: Total) => void;
  saveValues: (values: FormikValues, redirect?: string) => void;
  submenu: any;
}

/**
 * Wizard is a single Formik instance whose children are each page of the
 * multi-step form. The form is submitted on each forward transition (can only
 * progress with valid input), whereas a backwards step is allowed with
 * incomplete data. A snapshot of form state is used as initialValues after each
 * transition. Each page has an optional submit handler, and the top-level
 * submit is called when the final page is submitted.
 */
// eslint-disable-next-line max-len
export const Wizard: (props: WizardProps) => JSX.Element = ({ children, initialValues, onSubmit, error, notice, cartList, total, orderType, setTotal, saveValues, isPreRegisterOnly, submenu }: WizardProps): JSX.Element => {
  const [stepNumber, setStepNumber] = useState(0);
  const steps: ReactNode[] = React.Children.toArray(children);
  const [snapshot, setSnapshot] = useState(initialValues);
  const step = steps[stepNumber];
  const totalSteps = steps.length;
  const isLastStep = stepNumber === totalSteps - 1;
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Go to the next wizard step.
   */
  const next = (values: FormikValues): void => {
    setSnapshot(values);
    setStepNumber(Math.min(stepNumber + 1, totalSteps - 1));
  };

  /**
   * Go to the previous wizard step.
   */
  const previous = (values: FormikValues, e: React.MouseEvent<HTMLButtonElement, MouseEvent> = null): void => {
    setSnapshot(values);
    if (stepNumber > 0){
      setStepNumber(Math.max(stepNumber - 1, 0));
    } else if (e && e.currentTarget && e.currentTarget.name){
      setIsLoading(true);
      saveValues(values, e.currentTarget.name);
    }
  };

  /**
   * In case there is a onSubmit method specified on the
   * wizard step, call this method.
   */
  const handleSubmit = async (
    values: FormValues,
    formikHelpers: FormikHelpers<FormikValues>
  ): Promise<void> => {
    if (React.isValidElement(step) && step.props.onSubmit) {
      await step.props.onSubmit(values, formikHelpers);
    }
    if (isLastStep) {
      return onSubmit(values);
    }
    formikHelpers.setTouched({});
    next(values);

  };
  function cartItemIsCartListTraining(item: Article|CartListTraining): item is CartListTraining {
    return 'id_training' in item;
  }

  const overviewTrainings = cartList
    .filter(item => cartItemIsCartListTraining(item))
    .map((training: CartListTraining) => <OverviewItem
      key={training.id_training}
      id={training.id_training}
      subject={training.vak_naam}
      level={training.vak_niveau}
      title={`${training.product_type}`}
      subtitle={`${training.product_type ? getExamentrainingDatesLabel(training) : ''}`}
      price={training.prijs}
      subItems={training.subItems}
      deleteMethod={/* istanbul ignore next */ (id: string): Promise<void> => cart.removeTraining(id)}
    />);
  const overviewItems = cartList
    .filter(item => cartItemIsArticle(item))
    .map((article: Article) => <OverviewItem
      key={article.id_item}
      id={article.id_item}
      subject={article.subject}
      level={article.level}
      title={`${article.title}`}
      subtitle={''}
      price={article.price}
      deleteMethod={/* istanbul ignore next */ (id: string): Promise<void> => cart.removeShopItem(id)}
    />);

  return (
    <Formik
      initialValues={snapshot}
      onSubmit={handleSubmit}
      validationSchema={React.isValidElement(step) && step.props.validationSchema}>
      {(formik: FormikProps<FormValues>): JSX.Element => (
        <Fragment>
          <Progess step={stepNumber + 1}/>
          {submenu && stepNumber === 0 && submenu.items
          && <div className={'container py-1 pt-3'}>
            <div className={'row'}>
              <div className={'col-md-10 col-lg-8 col-md-offset-1 col-lg-offset-2 checkout__bekijk-meer'}>
                <span className={'title'}>Bekijk meer:</span>
                {submenu.items
                  .map((item: any) =>
                    <button
                      name={item.item.url}
                      key={item.item.title}
                      disabled={isLoading}
                      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => previous(formik.values, e)}
                    >
                      {item.item.title}
                    </button>)}
              </div>
            </div>
          </div>}

          <Form>
            <div className="container pt-3">
              <div className="row">
                <div className="col-md-12 col-lg-10 col-md-offset-0 col-lg-offset-1">
                  {error && <FormErrors messages={error}/>}
                  {notice
                  && <FormErrors messages={notice} isInfo={true}/>}
                  <div className={'row'}>
                    <div className={'col-12 col-md-8'}>
                      {step}
                    </div>
                    <div className={'col-12 col-md-4'}>
                      <h5 className={'checkout__title'}>Overzicht</h5>
                      <div className={'checkout__overview'}>
                        {overviewTrainings}
                        {overviewItems}
                        <CartTotal {...total} amountDonation={formik.values.donation.amount} />
                        <Field>
                          {({ form }: FieldProps<FormValues>): JSX.Element => (<Fragment>
                            <CouponForm form={form}
                              orderType={orderType}
                              setTotal={setTotal}/>
                          </Fragment>)}
                        </Field>
                      </div>
                      <div className="mt-2 d-flex w-100 align-items-center justify-content-between">
                        {stepNumber > 0 && (
                          <button className="btn btn-primary"
                            onClick={(): void => previous(formik.values)}
                            type="button">
                            Ga terug
                          </button>
                        )}
                        <button className="btn btn-primary gtm-ext-book-payment ml-auto"
                          disabled={formik.isSubmitting}
                          type="submit">
                          {isLastStep ? isPreRegisterOnly ? 'Reserveren' : 'Naar betalen' : 'Ga verder'}
                          {formik.isSubmitting && <i className="fa fa-spinner fa-pulse"/>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        </Fragment>
      )}
    </Formik>
  );
};

